import { ContentCopy, Edit, OpenInNew, Palette } from "@mui/icons-material";
import { Avatar, Box, Button, Chip, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { ReferenciaImagemMarketplace } from "../../../../enum/ReferenciaImagemMarketplace";
import { TipoMarketplaceCor, TipoMarketplaceDescricao, TipoMarketplaceTitulo } from "../../../../enum/TipoMarketplace";
import { TipoCobrancaEntregaTitulo } from "../../../../../pedidos/enum/TipoCobrancaEntrega";
import { useCopiar } from "../../../../../core/hooks/useCopiar";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import {
    AcoesContainer,
    CabecalhoPaper,
    CapaContainer,
    IdentificacaoContainer,
    ResumoContainer,
    ResumoItem,
    ResumoLabel
} from "../../styles";

interface CabecalhoMarketplaceProps {
    marketplace: IMarketplace;
}

export const CabecalhoMarketplace = ({ marketplace }: CabecalhoMarketplaceProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { real } = useMoeda();

    const configuracao = marketplace.configuracao;
    const imagens = configuracao?.personalizacao?.imagens;

    const logoUrl = imagens?.find(imagem => imagem.referencia == ReferenciaImagemMarketplace.logo)?.url;
    const capaUrl = imagens?.find(imagem => imagem.referencia == ReferenciaImagemMarketplace.capa)?.url;

    const aberto = configuracao?.status?.aberto ?? false;
    const corStatus = aberto ? theme.palette.success.main : theme.palette.error.main;

    const copiarLink = useCopiar(
        configuracao?.urlExterna ?? "Link não mapeado",
        "Link copiado para a área de transferência!"
    );

    const quantidadeMetodos = configuracao?.metodosPagamento?.length ?? 0;

    return (
        <CabecalhoPaper elevation={0}>
            <CapaContainer
                sx={{
                    backgroundImage: capaUrl ? `url(${capaUrl})` : "none",
                    background: capaUrl
                        ? undefined
                        : `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.85)}, ${alpha(theme.palette.secondary.main, 0.75)})`
                }}
            />
            <IdentificacaoContainer>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    {logoUrl ? (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt={configuracao?.nomePublico}
                            sx={{
                                width: 72,
                                height: 72,
                                mt: "-2.5rem",
                                objectFit: "contain",
                                borderRadius: "16px",
                                border: `3px solid ${theme.palette.background.paper}`,
                                backgroundColor: theme.palette.background.paper,
                                boxShadow: theme.shadows[2]
                            }}
                        />
                    ) : (
                        <Avatar
                            sx={{
                                width: 72,
                                height: 72,
                                mt: "-2.5rem",
                                fontSize: "1.75rem",
                                border: `3px solid ${theme.palette.background.paper}`,
                                boxShadow: theme.shadows[2]
                            }}
                        >
                            {configuracao?.nomePublico?.charAt(0)?.toUpperCase() || "?"}
                        </Avatar>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={600} noWrap>
                            {configuracao?.nomePublico ?? marketplace.nome}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.375,
                                    borderRadius: "999px",
                                    color: corStatus,
                                    backgroundColor: alpha(corStatus, 0.12)
                                }}
                            >
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: corStatus }} />
                                <Typography variant="caption" fontWeight={600}>
                                    {aberto ? "Aberto" : "Fechado"}
                                </Typography>
                            </Box>
                            <Tooltip title={TipoMarketplaceDescricao[marketplace.tipo]}>
                                <Chip
                                    size="small"
                                    label={TipoMarketplaceTitulo[marketplace.tipo]}
                                    sx={{ background: TipoMarketplaceCor[marketplace.tipo], color: "white" }}
                                />
                            </Tooltip>
                            {configuracao?.slug && (
                                <Typography variant="caption" color="text.secondary">
                                    /{configuracao.slug}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                <AcoesContainer>
                    <Tooltip title="Copiar link público">
                        <Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={copiarLink}>
                            Copiar link
                        </Button>
                    </Tooltip>
                    {configuracao?.urlExterna && (
                        <Tooltip title="Abrir cardápio em uma nova aba">
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<OpenInNew />}
                                href={configuracao.urlExterna}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Abrir cardápio
                            </Button>
                        </Tooltip>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Palette />}
                        onClick={() => navigate(`/marketplace/personalizar/${marketplace.uuid}`)}
                    >
                        Personalizar
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => navigate(`/marketplace/editar/${marketplace.uuid}`)}
                    >
                        Editar dados
                    </Button>
                </AcoesContainer>
            </IdentificacaoContainer>
            <ResumoContainer>
                <ResumoItem>
                    <ResumoLabel>Valor mínimo</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {real(configuracao?.valorMinimo)}
                    </Typography>
                </ResumoItem>
                <ResumoItem>
                    <ResumoLabel>Telefone</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {configuracao?.telefone ?? "-"}
                    </Typography>
                </ResumoItem>
                <ResumoItem>
                    <ResumoLabel>Formas de pagamento</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {quantidadeMetodos > 0 ? `${quantidadeMetodos} configurada(s)` : "Nenhuma"}
                    </Typography>
                </ResumoItem>
                <ResumoItem>
                    <ResumoLabel>Cobrança de entrega</ResumoLabel>
                    <Typography variant="body2" fontWeight={600}>
                        {configuracao?.tipoCobrancaEntrega
                            ? TipoCobrancaEntregaTitulo[configuracao.tipoCobrancaEntrega]
                            : "Não definida"}
                    </Typography>
                </ResumoItem>
            </ResumoContainer>
        </CabecalhoPaper>
    );
}
